// Reusable voice recorder with a Telegram/WhatsApp-style feel: tap the mic to
// start, see a live timer + animated waveform, then ➤ Send or 🗑 Cancel. Tap-based
// (not press-and-hold) for reliability across mouse + touch on the web.
// Calls onComplete(blob, seconds) on send; onError(message) on mic/codec failure.

import React, { useEffect, useRef, useState } from 'react';

const LEMON = '#f5b301';

function pickMimeType(): string {
  const MR = (window as any).MediaRecorder;
  if (!MR || !MR.isTypeSupported) return '';
  for (const m of ['audio/webm', 'audio/mp4', 'audio/ogg']) if (MR.isTypeSupported(m)) return m;
  return '';
}

interface Props {
  onComplete: (blob: Blob, seconds: number) => void;
  onError?: (message: string) => void;
  maxSeconds?: number;
  disabled?: boolean;
}

const VoiceRecorder: React.FC<Props> = ({ onComplete, onError, maxSeconds = 60, disabled }) => {
  const [recording, setRecording] = useState(false);
  const [starting, setStarting] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const mrRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const startedRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const cancelRef = useRef(false);
  const mimeRef = useRef('');

  const clearTimer = () => { if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null; } };
  useEffect(() => () => { clearTimer(); streamRef.current?.getTracks().forEach((t) => t.stop()); }, []);

  const start = async () => {
    if (disabled || starting || recording) return;
    if (!navigator.mediaDevices?.getUserMedia || !(window as any).MediaRecorder) {
      onError?.('Voice recording isn’t supported on this browser — type instead.');
      return;
    }
    setStarting(true);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setStarting(false);
      onError?.('Couldn’t access your microphone. Allow mic access, or type instead.');
      return;
    }
    try {
      streamRef.current = stream;
      const mime = pickMimeType();
      mimeRef.current = mime;
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mrRef.current = mr;
      chunksRef.current = [];
      cancelRef.current = false;
      startedRef.current = Date.now();
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        clearTimer();
        stream.getTracks().forEach((t) => t.stop());
        const seconds = Math.round((Date.now() - startedRef.current) / 1000);
        setRecording(false);
        setElapsed(0);
        if (cancelRef.current) return; // discarded
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || mime || 'audio/webm' });
        if (blob.size < 100) { onError?.('That recording was too short — try again.'); return; }
        onComplete(blob, seconds);
      };
      mr.start();
      setStarting(false);
      setRecording(true);
      setElapsed(0);
      timerRef.current = window.setInterval(() => {
        const s = Math.round((Date.now() - startedRef.current) / 1000);
        setElapsed(s);
        if (s >= maxSeconds) send();
      }, 250);
    } catch {
      setStarting(false);
      stream.getTracks().forEach((t) => t.stop());
      onError?.('Couldn’t start recording on this device — type instead.');
    }
  };

  const send = () => {
    if (mrRef.current && mrRef.current.state === 'recording') { cancelRef.current = false; mrRef.current.stop(); }
  };
  const cancel = () => {
    if (mrRef.current && mrRef.current.state === 'recording') { cancelRef.current = true; mrRef.current.stop(); }
  };

  const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (!recording) {
    return (
      <button
        type="button"
        onClick={start}
        disabled={disabled || starting}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
          borderRadius: 22, border: `1.5px solid ${LEMON}`, background: '#fff7e0',
          fontSize: 14, fontWeight: 600, color: '#0f172a', cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        {starting ? '…' : '🎙'} {starting ? 'Starting…' : 'Record a voice note'}
      </button>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 22, background: '#fef2f2', border: '1.5px solid #fca5a5' }}>
      <button type="button" onClick={cancel} title="Cancel" aria-label="Cancel"
        style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: '#64748b' }}>🗑</button>
      <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#dc2626', animation: 'lomiPulse 1s ease-in-out infinite' }} />
      <Waveform />
      <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 14, color: '#0f172a', minWidth: 38 }}>{mmss(elapsed)}</span>
      <button type="button" onClick={send} title="Send"
        style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 18, border: 'none', background: LEMON, color: '#1a1a1a', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}>
        ➤ Send
      </button>
      <style>{`@keyframes lomiPulse{0%,100%{opacity:1}50%{opacity:.3}}@keyframes lomiBar{0%,100%{transform:scaleY(.4)}50%{transform:scaleY(1)}}`}</style>
    </div>
  );
};

const Waveform: React.FC = () => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, height: 18 }}>
    {[0, 1, 2, 3, 4].map((i) => (
      <span key={i} style={{ width: 3, height: 16, borderRadius: 2, background: '#fca5a5', transformOrigin: 'center', animation: `lomiBar .9s ease-in-out ${i * 0.12}s infinite` }} />
    ))}
  </span>
);

export default VoiceRecorder;
